import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  getPlannedLessonDetails,
  getPlannedLessonPlacement,
  moveAppLessonToMatchPlan,
  retryPlannedLessonBuild,
  reviewPlannedLessonStory,
  revertPlanningRevision,
  updatePlannedLesson,
  updatePlanToMatchApp,
} from '@platform/client';
import {
  placementSchema,
  plannedLessonDetailsSchema,
  revertResultSchema,
} from '@platform/schema/curriculum';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { LessonVerification } from '../authoring/Verification';
import { shownDate } from '../library/feedback';
import { SCREEN_GAP } from '../routes/PageHeader';

import {
  ACTION_LABEL,
  drawerActions,
  isRunning,
  STATUS_LABEL,
  STORY_CHANGED,
} from './actions';
import { RemoveDialog } from './RemoveDialog';
import { StoryEditor } from './StoryEditor';

import type { DrawerAction } from './actions';
import type { RebuildRow } from './RebuildDialog';
import type {
  PlannedCourse,
  PlannedLesson,
  PlannedLessonDetails,
} from '@platform/schema/curriculum';
import type { ReactNode } from 'react';

const WIDTH = 560;
/** The spacing between the table and the AI panel, which the docked view keeps. */
export const PANEL_GAP = 2;

const day = (at: string | null): string => shownDate(at ?? undefined);

const when = (at: string): string => new Date(at).toLocaleString();

/**
 * The detailed view of one planned lesson — spec §10. Closing it with unsaved planning
 * edits asks first: CURR-23's locked proposal (a). Opened by Lesson ID, so a lesson the
 * table is not showing opens too: a link in the AI panel's answer (CURR-25).
 */
export function LessonDrawer({
  lessonId,
  courses,
  onClose,
  onBuild,
  onRebuild,
  onRemoved,
  notice,
  beside,
}: {
  lessonId: string | null;
  courses: readonly PlannedCourse[];
  /** Shown above the lesson: the AI panel's offer to move its scope here. */
  notice?: ReactNode;
  /**
   * The width of the AI panel at the screen's right edge. The view then opens beside it
   * rather than as a modal over it, which would hide the panel from sight and from
   * assistive technology — CURR-30.
   */
  beside?: string | undefined;
  onClose: () => void;
  onBuild: (lessonId: string) => void;
  onRebuild: (row: RebuildRow) => void;
  onRemoved: () => void;
}) {
  const [dirty, setDirty] = useState(false);
  // Fixed when the lesson opens: switching variant remounts the view and drops its edits.
  const [docked] = useState(beside !== undefined);
  const [confirming, setConfirming] = useState(false);
  const close = (): void => {
    if (dirty) setConfirming(true);
    else onClose();
  };
  const details = useQuery({
    queryKey: ['curriculum', 'lessons', 'details', lessonId],
    enabled: lessonId !== null,
    queryFn: async () =>
      plannedLessonDetailsSchema.parse(
        (
          await getPlannedLessonDetails({
            path: { lessonId: lessonId ?? '' },
            throwOnError: true,
          })
        ).data,
      ),
  });
  const current = details.data?.lesson;

  return (
    <>
      <Drawer
        anchor="right"
        open={lessonId !== null}
        onClose={close}
        variant={docked ? 'persistent' : 'temporary'}
        slotProps={{
          paper: {
            sx:
              beside === undefined
                ? {}
                : {
                    right: (theme) =>
                      `calc(${beside} + ${theme.spacing(SCREEN_GAP + PANEL_GAP)})`,
                  },
          },
        }}
      >
        {lessonId !== null && (
          <Stack
            spacing={2}
            sx={{ width: WIDTH, p: 3 }}
            role="region"
            aria-label="Lesson details"
          >
            <Typography variant="overline">
              Lesson ID {lessonId} · cannot change
            </Typography>
            {notice}
            {details.isPending && <LinearProgress />}
            {current !== undefined && (
              <Stack spacing={0.5}>
                <Typography variant="h6" component="h2">
                  {current.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {current.course} › {current.module}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                  <Chip size="small" label={STATUS_LABEL[current.status]} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      current.published
                        ? `Published ${day(current.publishedAt)}`
                        : 'Published: No'
                    }
                  />
                </Stack>
              </Stack>
            )}

            {details.isError && (
              <Alert severity="error">{describeApiError(details.error)}</Alert>
            )}
            {details.data !== undefined && (
              <StoryBeat lesson={details.data.lesson} />
            )}
            {details.data !== undefined && (
              <Loaded
                key={details.data.lesson.updatedAt}
                details={details.data}
                courses={courses}
                onDirtyChange={setDirty}
                onBuild={onBuild}
                onRebuild={onRebuild}
                onRemoved={onRemoved}
              />
            )}

            <Button sx={{ alignSelf: 'start' }} onClick={close}>
              Close
            </Button>
          </Stack>
        )}
      </Drawer>
      <DiscardDialog
        open={confirming}
        onKeep={() => {
          setConfirming(false);
        }}
        onDiscard={() => {
          setConfirming(false);
          onClose();
        }}
      />
    </>
  );
}

function Loaded({
  details,
  courses,
  onDirtyChange,
  onBuild,
  onRebuild,
  onRemoved,
}: {
  details: PlannedLessonDetails;
  courses: readonly PlannedCourse[];
  onDirtyChange: (dirty: boolean) => void;
  onBuild: (lessonId: string) => void;
  onRebuild: (row: RebuildRow) => void;
  onRemoved: () => void;
}) {
  const { lesson, build } = details;
  return (
    <>
      {build.planningChangedSince === true && (
        <Alert severity="warning">
          Planning changed since build. The built lesson still has what it was
          built from.
        </Alert>
      )}
      {lesson.storyChanged && (
        <Alert severity="warning">
          <strong>{STORY_CHANGED}</strong> {lesson.module}&apos;s story was
          saved again after this lesson was built. Rebuild it, or check it and
          mark it reviewed.
        </Alert>
      )}
      {lesson.status === 'BUILD_FAILED' && build.latestFailure !== null && (
        <Alert severity="error">
          Build failed {when(build.latestFailure.at)}:{' '}
          {build.latestFailure.message}
        </Alert>
      )}
      <Actions
        details={details}
        onBuild={onBuild}
        onRebuild={onRebuild}
        onRemoved={onRemoved}
      />
      <Divider />
      <PlanningForm
        lesson={lesson}
        courses={courses}
        onDirtyChange={onDirtyChange}
      />
      {lesson.placementMismatch && <PlacementSync lessonId={lesson.lessonId} />}
      <Divider />
      <Facts details={details} />
      {details.links !== null && (
        <>
          <Divider />
          <LessonVerification lessonId={details.links.builtLessonId} />
        </>
      )}
      <Divider />
      <History details={details} />
    </>
  );
}

/**
 * Outside `Loaded`, which remounts whenever the lesson's `updatedAt` moves: a beat being
 * typed must survive another session saving this lesson, so its save can be refused.
 */
function StoryBeat({ lesson }: { lesson: PlannedLesson }) {
  const queries = useQueryClient();
  return (
    <StoryEditor
      action={`Save story beat for ${lesson.lessonId}`}
      label="Story beat"
      helperText={`What happens in ${lesson.module}'s story in this lesson.`}
      minRows={3}
      saved={{ text: lesson.storyBeat, revision: lesson.moduleStoryRevision }}
      save={async (text, revision) => {
        await updatePlannedLesson({
          path: { lessonId: lesson.lessonId },
          body: { storyBeat: text, storyRevision: revision },
          throwOnError: true,
        });
        await queries.invalidateQueries({ queryKey: ['curriculum'] });
      }}
      load={async () => {
        const { data } = await getPlannedLessonDetails({
          path: { lessonId: lesson.lessonId },
          throwOnError: true,
        });
        const latest = plannedLessonDetailsSchema.parse(data).lesson;
        return { text: latest.storyBeat, revision: latest.moduleStoryRevision };
      }}
    />
  );
}

function Actions({
  details,
  onBuild,
  onRebuild,
  onRemoved,
}: {
  details: PlannedLessonDetails;
  onBuild: (lessonId: string) => void;
  onRebuild: (row: RebuildRow) => void;
  onRemoved: () => void;
}) {
  const queries = useQueryClient();
  const { lesson, links } = details;
  const [removing, setRemoving] = useState(false);
  const retry = useTrackedMutation(`Retry ${lesson.lessonId}`, {
    mutationFn: () =>
      retryPlannedLessonBuild({
        path: { lessonId: lesson.lessonId },
        throwOnError: true,
      }),
    onSuccess: () =>
      queries.invalidateQueries({ queryKey: ['curriculum', 'lessons'] }),
  });
  const review = useTrackedMutation(`Mark ${lesson.lessonId} reviewed`, {
    mutationFn: () =>
      reviewPlannedLessonStory({
        path: { lessonId: lesson.lessonId },
        body: { storyRevision: lesson.moduleStoryRevision },
        throwOnError: true,
      }),
    onSuccess: () =>
      queries.invalidateQueries({ queryKey: ['curriculum', 'lessons'] }),
  });

  const button = (kind: DrawerAction, disabledReason: string | null) => {
    const common = {
      size: 'small' as const,
      variant: 'outlined' as const,
      disabled:
        disabledReason !== null ||
        (kind === 'retry' && retry.isPending) ||
        (kind === 'reviewStory' && review.isPending),
    };
    // Try plays the lesson: the editor opens on its play mode, the viewer beside it.
    const path = links?.editorPath;
    const control =
      (kind === 'try' || kind === 'open' || kind === 'edit') &&
      path !== undefined &&
      disabledReason === null ? (
        <Button
          {...common}
          component={RouterLink}
          to={path}
          {...(kind === 'try' ? { target: '_blank', rel: 'noreferrer' } : {})}
        >
          {ACTION_LABEL[kind]}
        </Button>
      ) : kind === 'source' && disabledReason === null ? (
        <Button
          {...common}
          href={lesson.primarySourceUrl}
          target="_blank"
          rel="noreferrer"
        >
          {ACTION_LABEL[kind]}
        </Button>
      ) : (
        <Button
          {...common}
          color={kind === 'remove' ? 'error' : 'primary'}
          onClick={() => {
            if (kind === 'build') onBuild(lesson.lessonId);
            if (kind === 'rebuild') onRebuild(lesson);
            if (kind === 'retry') retry.mutate();
            if (kind === 'reviewStory') review.mutate();
            if (kind === 'remove') setRemoving(true);
          }}
        >
          {ACTION_LABEL[kind]}
        </Button>
      );
    return disabledReason === null ? (
      <span key={kind}>{control}</span>
    ) : (
      <Tooltip key={kind} title={disabledReason}>
        <span>{control}</span>
      </Tooltip>
    );
  };

  const actions = drawerActions(lesson);
  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        useFlexGap
        spacing={1}
        sx={{ flexWrap: 'wrap' }}
        role="group"
        aria-label="Lesson actions"
      >
        {actions.map(({ kind, disabledReason }) =>
          button(kind, disabledReason),
        )}
      </Stack>
      {links !== null && !lesson.published && (
        <Typography variant="caption" color="text.secondary">
          Try lesson opens the built draft – not published.
        </Typography>
      )}
      <Stack component="ul" sx={{ m: 0, pl: 2.5 }} aria-label="Why off">
        {actions.flatMap(({ kind, disabledReason }) =>
          disabledReason === null
            ? []
            : [
                <Typography component="li" variant="caption" key={kind}>
                  {ACTION_LABEL[kind]}: {disabledReason}
                </Typography>,
              ],
        )}
      </Stack>
      {retry.isError && (
        <Alert severity="error">{describeApiError(retry.error)}</Alert>
      )}
      {review.isError && (
        <Alert severity="error">{describeApiError(review.error)}</Alert>
      )}
      <RemoveDialog
        lesson={removing ? lesson : null}
        onClose={() => {
          setRemoving(false);
        }}
        onRemoved={onRemoved}
      />
    </Stack>
  );
}

type Fields = Pick<
  PlannedLesson,
  | 'name'
  | 'description'
  | 'writerBrief'
  | 'primarySourceName'
  | 'primarySourceUrl'
  | 'additionalSourceName'
  | 'additionalSourceUrl'
  | 'courseId'
  | 'moduleId'
  | 'extra'
>;

export const PLANNING_TEXT: {
  key: keyof Fields;
  label: string;
  rows?: number;
}[] = [
  { key: 'name', label: 'Lesson name' },
  { key: 'description', label: 'Description', rows: 3 },
  { key: 'writerBrief', label: 'Writer brief', rows: 6 },
  { key: 'primarySourceName', label: 'Primary source name' },
  { key: 'primarySourceUrl', label: 'Primary source URL' },
  { key: 'additionalSourceName', label: 'Additional source name' },
  { key: 'additionalSourceUrl', label: 'Additional source URL' },
];

const fieldsOf = (lesson: PlannedLesson): Fields => ({
  name: lesson.name,
  description: lesson.description,
  writerBrief: lesson.writerBrief,
  primarySourceName: lesson.primarySourceName,
  primarySourceUrl: lesson.primarySourceUrl,
  additionalSourceName: lesson.additionalSourceName,
  additionalSourceUrl: lesson.additionalSourceUrl,
  courseId: lesson.courseId,
  moduleId: lesson.moduleId,
  extra: lesson.extra,
});

type Changes = Parameters<typeof updatePlannedLesson>[0]['body'];

/** Only what changed is sent, so the revision names only what the admin edited. */
function changesOf(was: Fields, now: Fields): Changes {
  const changes: Changes = {};
  for (const { key } of PLANNING_TEXT) {
    const value = now[key];
    if (typeof value === 'string' && value !== was[key])
      Object.assign(changes, { [key]: value });
  }
  if (now.moduleId !== was.moduleId) changes.moduleId = now.moduleId;
  if (JSON.stringify(now.extra) !== JSON.stringify(was.extra))
    changes.extra = now.extra;
  return changes;
}

function PlanningForm({
  lesson,
  courses,
  onDirtyChange,
}: {
  lesson: PlannedLesson;
  courses: readonly PlannedCourse[];
  onDirtyChange: (dirty: boolean) => void;
}) {
  const queries = useQueryClient();
  const saved = fieldsOf(lesson);
  const [fields, setFields] = useState(saved);
  const changes = changesOf(saved, fields);
  const dirty = Object.keys(changes).length > 0;
  const locked = isRunning(lesson.status);
  const modules =
    courses.find((course) => course.id === fields.courseId)?.modules ?? [];

  useEffect(() => {
    onDirtyChange(dirty);
    return () => {
      onDirtyChange(false);
    };
  }, [dirty, onDirtyChange]);

  const save = useTrackedMutation(`Save ${lesson.lessonId}`, {
    mutationFn: () =>
      updatePlannedLesson({
        path: { lessonId: lesson.lessonId },
        body: changes,
        throwOnError: true,
      }),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['curriculum'] }),
  });

  const set = (patch: Partial<Fields>) => {
    setFields((was) => ({ ...was, ...patch }));
  };

  return (
    <Stack
      component="form"
      spacing={2}
      aria-label="Planning fields"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Typography variant="subtitle2" component="h3">
        Planning
      </Typography>
      {locked && (
        <Alert severity="info">
          Editing is paused while this lesson builds.
        </Alert>
      )}
      <Stack direction="row" spacing={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Course"
          value={fields.courseId}
          disabled={locked}
          onChange={(event) => {
            set({ courseId: event.target.value, moduleId: '' });
          }}
        >
          {courses.map((course) => (
            <MenuItem key={course.id} value={course.id}>
              {course.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="Module"
          value={fields.moduleId}
          disabled={locked}
          error={fields.moduleId === ''}
          helperText={
            fields.moduleId === '' ? 'Choose a module in this course.' : ' '
          }
          onChange={(event) => {
            set({ moduleId: event.target.value });
          }}
        >
          {modules.map((module) => (
            <MenuItem key={module.id} value={module.id}>
              {module.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {PLANNING_TEXT.map(({ key, label, rows }) => (
        <TextField
          key={key}
          size="small"
          label={label}
          value={fields[key]}
          disabled={locked}
          multiline={rows !== undefined}
          minRows={rows}
          onChange={(event) => {
            set({ [key]: event.target.value });
          }}
        />
      ))}
      {fields.extra.map((column, index) => (
        <TextField
          key={column.column}
          size="small"
          label={column.column}
          value={column.value}
          disabled={locked}
          onChange={(event) => {
            set({
              extra: fields.extra.map((one, at) =>
                at === index ? { ...one, value: event.target.value } : one,
              ),
            });
          }}
        />
      ))}
      {save.isError && (
        <Alert severity="error">{describeApiError(save.error)}</Alert>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          type="submit"
          variant="contained"
          disabled={
            !dirty || locked || fields.moduleId === '' || save.isPending
          }
        >
          Save changes
        </Button>
        <Button
          disabled={!dirty || save.isPending}
          onClick={() => {
            setFields(saved);
            save.reset();
          }}
        >
          Undo changes
        </Button>
      </Stack>
    </Stack>
  );
}

/** Spec §6: both placements and both syncs, each saying what it would do first. */
function PlacementSync({ lessonId }: { lessonId: string }) {
  const queries = useQueryClient();
  const placement = useQuery({
    queryKey: ['curriculum', 'lessons', 'placement', lessonId],
    queryFn: async () =>
      placementSchema.parse(
        (
          await getPlannedLessonPlacement({
            path: { lessonId },
            throwOnError: true,
          })
        ).data,
      ),
  });
  const sync = useTrackedMutation(`Sync the placement of ${lessonId}`, {
    mutationFn: (direction: 'plan' | 'app') =>
      direction === 'plan'
        ? updatePlanToMatchApp({ path: { lessonId }, throwOnError: true })
        : moveAppLessonToMatchPlan({ path: { lessonId }, throwOnError: true }),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['curriculum'] }),
  });

  const data = placement.data;
  return (
    <Alert severity="warning" role="region" aria-label="Placement mismatch">
      <Stack spacing={1}>
        <Typography variant="body2">
          The plan and the app have this lesson in different modules.
        </Typography>
        {data !== undefined && (
          <>
            <Typography variant="body2">
              Plan: {data.planned.course} › {data.planned.module}
              <br />
              App:{' '}
              {data.app
                .map((one) =>
                  one.course === null
                    ? one.module
                    : `${one.course} › ${one.module}`,
                )
                .join('; ')}
            </Typography>
            {(
              [
                ['plan', 'Update plan to match app', data.updatePlan],
                ['app', 'Move app lesson to match plan', data.moveAppLesson],
              ] as const
            ).map(([direction, label, option]) => (
              <Stack key={direction} spacing={0.5}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: 'start' }}
                  disabled={!option.available || sync.isPending}
                  onClick={() => {
                    sync.mutate(direction);
                  }}
                >
                  {label}
                </Button>
                <Typography variant="caption">{option.message}</Typography>
              </Stack>
            ))}
          </>
        )}
        {(placement.isError || sync.isError) && (
          <Typography variant="body2" color="error">
            {describeApiError(placement.error ?? sync.error)}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
}

function Facts({ details }: { details: PlannedLessonDetails }) {
  const { build, approval, publications, learners, links } = details;
  const rows: [string, ReactNode][] = [
    [
      'Built',
      build.builtAt === null
        ? 'Not built'
        : `${when(build.builtAt)}${links === null ? '' : ` · version ${String(links.version)}`}`,
    ],
    [
      'Approval',
      approval?.status == null
        ? '—'
        : `${approval.status}${approval.by === null ? '' : ` by ${approval.by}`}${approval.at === null ? '' : `, ${day(approval.at)}`}`,
    ],
    [
      'Publication',
      publications.length === 0
        ? 'Never published'
        : publications
            .map(
              (one) =>
                `${one.kind === 'PUBLISHED' ? 'Published' : 'Unpublished'} v${String(one.version)} ${day(one.at)}`,
            )
            .join('; '),
    ],
    [
      'Learners (All time)',
      learners === null
        ? 'Not available'
        : `${String(learners.started)} started · ${String(learners.completed)} completed`,
    ],
  ];
  return (
    <Stack spacing={0.5} component="dl" sx={{ m: 0 }}>
      {rows.map(([term, value]) => (
        <Stack key={term} direction="row" spacing={1}>
          <Typography
            component="dt"
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 150 }}
          >
            {term}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0 }}>
            {value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function History({ details }: { details: PlannedLessonDetails }) {
  const queries = useQueryClient();
  const [notes, setNotes] = useState<string[]>([]);
  const locked = isRunning(details.lesson.status);
  const revert = useTrackedMutation('Revert a planning edit', {
    mutationFn: async (id: string) =>
      revertResultSchema.parse(
        (await revertPlanningRevision({ path: { id }, throwOnError: true }))
          .data,
      ),
    onSuccess: async (result) => {
      setNotes(result.notes);
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
    },
  });

  return (
    <Stack spacing={1} role="region" aria-label="History">
      <Typography variant="subtitle2" component="h3">
        History
      </Typography>
      {details.history.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nothing yet.
        </Typography>
      )}
      <Stack component="ol" spacing={1} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
        {details.history.map((entry) => (
          <Stack
            component="li"
            key={`${entry.at}-${entry.kind}`}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="body2">
              {when(entry.at)} · {entry.what}
              {entry.who === null ? '' : ` · ${entry.who}`}
            </Typography>
            {entry.revisionId !== null && (
              <Button
                size="small"
                disabled={locked || revert.isPending}
                onClick={() => {
                  if (entry.revisionId !== null)
                    revert.mutate(entry.revisionId);
                }}
              >
                Revert
              </Button>
            )}
          </Stack>
        ))}
      </Stack>
      {notes.map((note) => (
        <Alert key={note} severity="info">
          {note}
        </Alert>
      ))}
      {revert.isError && (
        <Alert severity="error">{describeApiError(revert.error)}</Alert>
      )}
    </Stack>
  );
}

function DiscardDialog({
  open,
  onKeep,
  onDiscard,
}: {
  open: boolean;
  onKeep: () => void;
  onDiscard: () => void;
}) {
  return (
    <Dialog open={open} onClose={onKeep}>
      <DialogTitle>Discard unsaved changes?</DialogTitle>
      <DialogContent>
        <Typography>
          Your edits to this lesson’s planning are not saved.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onKeep}>Keep editing</Button>
        <Button color="error" onClick={onDiscard}>
          Discard
        </Button>
      </DialogActions>
    </Dialog>
  );
}
